import { useEffect, useMemo } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { useAuth } from "@/contexts/AuthContext";
import {
  canEditDocuments,
  canPublishDocuments,
  canRestoreDocuments,
} from "@/lib/document/documentRoles";
import { DocumentBlockList } from "./DocumentBlockList";
import { DocumentExportHistorySection } from "./DocumentExportHistorySection";
import { DocumentTagsField } from "./DocumentTagsField";
import { DocumentVariablesEditor } from "./DocumentVariablesEditor";
import { DocumentVersionPanel } from "./DocumentVersionPanel";
import { DocumentFormBridgeSection } from "../bridge/DocumentFormBridgeSection";
import {
  DocumentEditorSaveFeedback,
  DocumentEditorSavedLabel,
  saveFlashButtonClass,
} from "./DocumentEditorSaveFeedback";
import { DocumentEditorPdfPreview } from "./DocumentEditorPdfPreview";
import { useDocumentDraftEditor } from "./useDocumentDraftEditor";
import {
  formatDocCategoryLabel,
} from "@/lib/document/documentCategoryOptions";

type Props = {
  /** Load an existing document on mount; omit for a blank new document. */
  initialDocumentId?: string | null;
  /** When set, shows a back control in the top toolbar (Documents browse view). */
  onBack?: () => void;
};

export function DocumentEditorWorkspace({ initialDocumentId = null, onBack }: Props) {
  const auth = useAuth();
  const editor = useDocumentDraftEditor();
  const canEdit = canEditDocuments(auth);
  const canPublish = canPublishDocuments(auth);
  const canRestore = canRestoreDocuments(auth);
  const canAuthor = Boolean(editor.document && canEdit);

  const previewVersionId =
    editor.currentDraftVersionId ??
    editor.document?.published_version_id ??
    editor.document?.id ??
    "";
  const previewVersionNumber = useMemo(() => {
    if (editor.versionNumber != null) return editor.versionNumber;
    const publishedId = editor.document?.published_version_id;
    if (!publishedId) return 0;
    return editor.versions.find((v) => v.id === publishedId)?.version_number ?? 0;
  }, [editor.versionNumber, editor.document?.published_version_id, editor.versions]);

  useEffect(() => {
    if (editor.saveFlash !== "metadata") return;
    const t = window.setTimeout(() => editor.dismissSaveFeedback(), 1000);
    return () => window.clearTimeout(t);
  }, [editor.saveFlash, editor.dismissSaveFeedback]);

  useEffect(() => {
    if (initialDocumentId) {
      void editor.loadDocument(initialDocumentId);
    }
  }, [initialDocumentId]); // eslint-disable-line react-hooks/exhaustive-deps -- load once per id

  return (
    <div className="space-y-5">
      <DocumentEditorSaveFeedback
        message={editor.banner}
        onDismiss={editor.dismissSaveFeedback}
      />
      {onBack || editor.openDocuments.length > 0 ? (
        <div className="flex flex-wrap items-center gap-3">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50 dark:border-zinc-600 dark:hover:bg-zinc-800"
            >
              ← Back to documents
            </button>
          ) : null}
          {editor.openDocuments.length > 0 ? (
            <label className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-2 text-sm sm:flex-nowrap">
              <span className="font-medium text-gray-700 dark:text-zinc-300">Open existing</span>
              <select
                value={editor.document?.id ?? ""}
                onChange={(e) => {
                  const id = e.target.value;
                  if (id) void editor.loadDocument(id);
                  else editor.resetNewDocument();
                }}
                className="min-w-[12rem] max-w-full rounded-md border border-gray-200 px-3 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-900"
              >
                <option value="">Create new…</option>
                {editor.openDocuments.map((doc) => (
                  <option key={doc.id} value={doc.id}>
                    {doc.name}
                    {doc.published_version_id ? " (published)" : ""}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
      ) : null}

      <SectionCard
        title={editor.document ? "Document details" : "New document"}
        description={
          editor.document
            ? "Name, category, and tags. Block content is saved separately as a draft version below."
            : "Pick a category folder, name the document, then author blocks below."
        }
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="font-medium text-gray-700 dark:text-zinc-300">Name</span>
            <input
              type="text"
              value={editor.name}
              onChange={(e) => editor.setName(e.target.value)}
              disabled={!canEdit}
              className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm disabled:opacity-60 dark:border-zinc-600 dark:bg-zinc-900"
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-gray-700 dark:text-zinc-300">Category</span>
            <select
              value={editor.categoryId}
              onChange={(e) => editor.setCategoryId(e.target.value)}
              disabled={!canEdit}
              className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm disabled:opacity-60 dark:border-zinc-600 dark:bg-zinc-900"
            >
              <option value="">Select category…</option>
              {editor.categoryOptions.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {formatDocCategoryLabel(cat.name, cat.depth)}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm sm:col-span-2">
            <span className="font-medium text-gray-700 dark:text-zinc-300">Library summary</span>
            <textarea
              value={editor.description}
              onChange={(e) => editor.setDescription(e.target.value)}
              disabled={!canEdit}
              rows={3}
              placeholder="Optional note for library browse"
              className="mt-1 min-h-[4.5rem] w-full resize-y rounded-md border border-gray-200 px-3 py-2 text-sm disabled:opacity-60 dark:border-zinc-600 dark:bg-zinc-900"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
              Shown in the document library only — not included in exported PDFs.
            </p>
          </label>
          <DocumentTagsField
            value={editor.tags}
            onChange={editor.setTags}
            disabled={!canEdit}
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {!editor.document ? (
            <button
              type="button"
              disabled={editor.saving || !canEdit}
              onClick={() => void editor.createDocument()}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {editor.saving ? "Creating…" : "Create document"}
            </button>
          ) : (
            <>
              <button
                type="button"
                disabled={editor.saving || !canEdit}
                onClick={() => void editor.saveMetadata()}
                className={saveFlashButtonClass(
                  editor.saveFlash === "metadata",
                  "rounded-md border border-gray-300 px-4 py-2 text-sm transition-colors duration-300 dark:border-zinc-600",
                )}
              >
                Save details
              </button>
              <button
                type="button"
                disabled={editor.saving || !canEdit}
                onClick={() => editor.resetNewDocument()}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm dark:border-zinc-600"
              >
                New document
              </button>
            </>
          )}
          {!editor.document ? (
            <DocumentEditorSavedLabel show={editor.saveFlash === "create"} />
          ) : null}
        </div>
        {!canEdit ? (
          <p className="mt-3 text-sm text-amber-800 dark:text-amber-200">
            Your role can browse the document library but not author drafts. Ask an org admin for
            editor access.
          </p>
        ) : null}
      </SectionCard>

      {editor.document ? (
        <>
          <SectionCard
            title="Versions"
            description={
              editor.hasPersistedDraft
                ? "Publish makes the draft read-only in the library. Restore forks a published snapshot into a new draft."
                : "After you add blocks, use Save draft version below — then publish here to release the document."
            }
          >
            <DocumentVersionPanel
              versions={editor.versions}
              currentDraftVersionId={editor.currentDraftVersionId}
              canPublish={canPublish}
              canRestore={canRestore}
              busy={editor.saving}
              compareLeftId={editor.compareLeftId}
              compareRightId={editor.compareRightId}
              onCompareLeftChange={editor.setCompareLeftId}
              onCompareRightChange={editor.setCompareRightId}
              onPublish={(id) => void editor.publishDraft(id)}
              onRestore={(id) => void editor.restoreVersion(id)}
            />
          </SectionCard>

          <DocumentFormBridgeSection
            documentId={editor.document.id}
            publishedVersionId={editor.document.published_version_id}
            hasDraft={editor.hasPersistedDraft}
          />

          <SectionCard
            title="Export history"
            description="Registered PDF exports from the library fill flow (doc_exports)."
          >
            <DocumentExportHistorySection
              documentId={editor.document.id}
              versionId={editor.document.published_version_id}
            />
          </SectionCard>

          <SectionCard
            title="Template variables"
            description="Define keys for export-time fill. Block fill fields (text, freeform, signature, date) sync their keys here on save."
          >
            {canAuthor ? (
              <DocumentVariablesEditor
                variables={editor.variables}
                onChange={editor.setVariables}
                disabled={editor.saving || !canEdit}
              />
            ) : (
              <p className="text-sm text-gray-600 dark:text-zinc-300">
                Create the document first to define template variables.
              </p>
            )}
            {!editor.hasPersistedDraft && canAuthor ? (
              <p className="mt-2 text-xs text-gray-500 dark:text-zinc-400">
                Variables are saved when you click Save draft version in the block editor.
              </p>
            ) : null}
          </SectionCard>

          <SectionCard
            title="Block editor"
            description={
              editor.hasPersistedDraft
                ? `Editing draft version ${editor.versionNumber ?? "?"}. Published versions are read-only.`
                : "Add blocks below, then save a draft version to unlock publishing and form links."
            }
          >
            <div {...{ [FIREFLY_TARGET_ATTR]: "doc-block-editor" }}>
              {canAuthor ? (
                <DocumentBlockList
                  blocks={editor.blocks}
                  assets={editor.assets}
                  onAddBlock={editor.addBlock}
                  onMoveBlock={editor.moveBlock}
                  onRemoveBlock={editor.removeBlock}
                  onUpdateBlock={editor.updateBlock}
                />
              ) : (
                <p className="text-sm text-gray-600 dark:text-zinc-300">
                  Block editing requires editor access.
                </p>
              )}

              {canAuthor ? (
                <>
                  <label className="mt-4 block text-sm">
                    <span className="font-medium text-gray-700 dark:text-zinc-300">Change notes</span>
                    <input
                      type="text"
                      value={editor.changeNotes}
                      onChange={(e) => editor.setChangeNotes(e.target.value)}
                      placeholder="Optional summary for this save"
                      className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                    />
                  </label>

                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      disabled={editor.saving}
                      onClick={() => void editor.saveDraftVersion()}
                      className={saveFlashButtonClass(
                        editor.saveFlash === "draft",
                        "rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-300 hover:bg-indigo-700 disabled:opacity-50",
                      )}
                    >
                      {editor.saving
                        ? "Saving…"
                        : editor.hasPersistedDraft
                          ? "Save draft version"
                          : "Save first draft version"}
                    </button>
                    <DocumentEditorPdfPreview
                      documentId={editor.document!.id}
                      documentName={editor.name || "Document"}
                      blocks={editor.blocks}
                      variables={editor.variables}
                      assets={editor.assets}
                      previewVersionId={previewVersionId}
                      previewVersionNumber={previewVersionNumber}
                      disabled={editor.saving}
                    />
                    {editor.hasPersistedDraft ? (
                      <DocumentEditorSavedLabel show={editor.saveFlash === "draft"} />
                    ) : null}
                  </div>
                </>
              ) : null}
            </div>
          </SectionCard>
        </>
      ) : null}

      {editor.error ? (
        <p className="text-sm text-red-700 dark:text-red-300">{editor.error}</p>
      ) : null}
      {editor.loading ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">Loading document…</p>
      ) : null}
    </div>
  );
}
